import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Database, TrendingUp, CalendarCheck2, MessageSquareText, Users, Activity, ShieldCheck, RefreshCw } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return "—"; }
};

const statusColor = {
  draft:     "bg-neutral-700 text-neutral-100 border-neutral-500",
  active:    "bg-emerald-500/20 text-emerald-300 border-emerald-400/50",
  paused:    "bg-amber-500/20 text-amber-300 border-amber-400/50",
  completed: "bg-blue-500/20 text-blue-300 border-blue-400/50",
};

export default function ReactivationPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data: d } = await axios.get(`${API_BASE}/api/reactivation/public/portal/${token}`);
      setData(d);
      setError("");
    } catch (e) {
      setError(e.response?.data?.detail || "Unable to load this portal. The link may have been revoked.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);
  // Poll every 60 seconds while tab is open
  useEffect(() => {
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  if (loading) return <PortalShell><div className="text-neutral-400 text-center py-16">Loading portal…</div></PortalShell>;
  if (error) return <PortalShell><div className="text-rose-300 border border-rose-500/50 bg-rose-950/40 p-6 text-center max-w-md mx-auto" data-testid="portal-error">{error}</div></PortalShell>;
  if (!data) return null;

  const { campaign, stats, recent_appointments, as_of } = data;
  const brandColor = (campaign.brand_color && /^#[0-9A-Fa-f]{6}$/.test(campaign.brand_color)) ? campaign.brand_color : "#F97316";
  const totalWorked = stats.leads_attempted || 0;
  const totalLeads = stats.leads_total || 0;
  const workedPct = totalLeads ? Math.min(100, Math.round((totalWorked / totalLeads) * 100)) : 0;

  return (
    <PortalShell>
      {/* Header */}
      <div className="mb-10" data-testid="portal-header">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4">
            {campaign.logo_url ? (
              <img
                src={campaign.logo_url}
                alt={`${campaign.client_business_name} logo`}
                className="w-16 h-16 object-contain border border-neutral-800 bg-white p-1"
                data-testid="portal-logo"
                onError={(e) => { e.target.style.display = "none"; }}
              />
            ) : null}
            <div>
              <div className="text-xs font-mono uppercase tracking-widest mb-2 flex items-center gap-2" style={{ color: brandColor }}>
                <Database className="w-3.5 h-3.5" /> Reactivation dashboard
              </div>
              <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight text-white mb-2">
                {campaign.client_business_name || campaign.name}
              </h1>
              <div className="text-neutral-400 text-sm">{campaign.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-xs font-mono uppercase tracking-widest px-3 py-1.5 border ${statusColor[campaign.status] || statusColor.draft}`} data-testid="portal-status">
              {campaign.status}
            </span>
            <button
              onClick={load}
              disabled={refreshing}
              className="text-neutral-300 hover:text-white text-xs inline-flex items-center gap-1.5 border border-neutral-700 hover:border-neutral-500 px-3 py-1.5 disabled:opacity-50"
              data-testid="portal-refresh"
            >
              <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="portal-kpis">
        <Kpi icon={Users}           label="Leads reached out" value={stats.leads_attempted} tone="blue" testId="kpi-attempted" />
        <Kpi icon={MessageSquareText} label="Responded"       value={stats.leads_responded} tone="amber" testId="kpi-responded" />
        <Kpi icon={CalendarCheck2}  label="Appointments booked" value={stats.appointments_total} tone="emerald" testId="kpi-booked" />
        <Kpi icon={TrendingUp}      label="Response rate"     value={`${stats.response_rate}%`} tone="violet" testId="kpi-rate" />
      </div>

      {/* Progress + rates */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        <div className="md:col-span-2 border border-neutral-800 bg-neutral-900/70 p-5">
          <div className="text-xs font-mono uppercase tracking-widest text-neutral-400 mb-3 flex items-center gap-2">
            <Activity className="w-3.5 h-3.5" /> List progress
          </div>
          <div className="text-3xl font-bold text-white">
            {totalWorked} <span className="text-neutral-500 text-lg">/ {totalLeads} leads worked</span>
          </div>
          <div className="mt-4 h-2 bg-neutral-800 rounded-full overflow-hidden">
            <div
              className="h-full transition-all"
              style={{ width: `${workedPct}%`, background: `linear-gradient(90deg, ${brandColor}, ${brandColor}CC)` }}
              data-testid="portal-progress-bar"
            />
          </div>
          <div className="text-xs font-mono text-neutral-500 mt-2">{workedPct}% of your list</div>
        </div>
        <div className="border border-neutral-800 bg-neutral-900/70 p-5">
          <div className="text-xs font-mono uppercase tracking-widest text-neutral-400 mb-3">Conversion funnel</div>
          <div className="space-y-2 text-sm">
            <FunnelRow label="Contact rate"  value={`${stats.contact_rate}%`} desc="of your list attempted" />
            <FunnelRow label="Response rate" value={`${stats.response_rate}%`} desc="of contacted replied" />
            <FunnelRow label="Book rate"     value={`${stats.book_rate}%`} desc="of responders booked" />
          </div>
        </div>
      </div>

      {/* Recent appointments */}
      <div className="border border-neutral-800 bg-neutral-900/70 mb-10" data-testid="portal-appointments">
        <div className="px-5 py-3 border-b border-neutral-800 text-xs font-mono uppercase tracking-widest text-neutral-400 flex items-center gap-2">
          <CalendarCheck2 className="w-3.5 h-3.5" /> Recent bookings
        </div>
        {recent_appointments.length === 0 ? (
          <div className="px-5 py-10 text-center text-neutral-500 text-sm">
            No booked appointments yet — the sequence is still working through your list.
          </div>
        ) : (
          <ul className="divide-y divide-neutral-800">
            {recent_appointments.map((a, i) => (
              <li key={i} className="px-5 py-3 flex items-center justify-between text-sm" data-testid={`portal-appt-${i}`}>
                <div>
                  <div className="text-white font-medium">{a.first_name}</div>
                  <div className="text-xs text-neutral-500">{a.service_type || "Service"}</div>
                </div>
                <div className="text-right">
                  <div className="text-neutral-300 text-xs">{a.appointment_at || fmtDate(a.created_at)}</div>
                  <div className="text-neutral-500 text-[10px] font-mono uppercase">Booked {fmtDate(a.created_at)}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-neutral-500 font-mono border-t border-neutral-800 pt-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5" /> Live snapshot · refreshes every 60s
        </div>
        <div>As of {fmtDate(as_of)}</div>
      </div>
    </PortalShell>
  );
}

function Kpi({ icon: Icon, label, value, tone, testId }) {
  const tones = {
    blue:    "border-blue-400/40 bg-blue-500/10",
    amber:   "border-amber-400/40 bg-amber-500/10",
    emerald: "border-emerald-400/40 bg-emerald-500/10",
    violet:  "border-violet-400/40 bg-violet-500/10",
  };
  return (
    <div className={`border ${tones[tone] || tones.blue} p-4`} data-testid={testId}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-300 mb-1.5 flex items-center gap-1.5">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="text-3xl font-bold text-white">{value}</div>
    </div>
  );
}

function FunnelRow({ label, value, desc }) {
  return (
    <div className="flex items-baseline justify-between">
      <div>
        <div className="text-white font-medium text-sm">{label}</div>
        <div className="text-[10px] text-neutral-500">{desc}</div>
      </div>
      <div className="text-emerald-300 font-mono font-bold">{value}</div>
    </div>
  );
}

function PortalShell({ children }) {
  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100" data-testid="reactivation-portal">
      <div className="max-w-5xl mx-auto px-6 py-10 md:py-14">
        {children}
        <div className="mt-10 text-center text-[10px] text-neutral-600 font-mono uppercase tracking-widest">
          Powered by Clients OnRamp
        </div>
      </div>
    </div>
  );
}
